import React, { useState, useEffect } from 'react';
import { FiUsers, FiFileText, FiSettings, FiBarChart2, FiLogOut, FiMenu, FiX, FiEdit2, FiTrash2, FiSearch, FiRefreshCw, FiHome, FiPlusSquare, FiMessageSquare } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL, API_ADMIN_END_POINT, API_END_POINT } from '../Utils/Constant';
import Overview from './components/Overview';
import Users from './components/Users';
import ContentWriters from './components/ContentWriters';
import Blogs from './components/Blogs';
import Settings from './components/Settings';
import QueryDetails from './components/QueryDetails';

// Import BlogCreate and BlogEdit components from AstroBlog
import BlogCreate from '../AstroBlog/BlogCreate';
import BlogEdit from '../AstroBlog/BlogEdit';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalBlogs: 0,
    totalContentWriters: 0,
    recentBlogs: 0
  });
  const [users, setUsers] = useState([]);
  const [contentWriters, setContentWriters] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [newContentWriter, setNewContentWriter] = useState({
    username: '',
    email: '',
    password: '',
    name: ''
  });
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [editingWriter, setEditingWriter] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);

  useEffect(() => {
    fetchDashboardData();

    // Check if admin is authenticated, if not redirect to login
    const adminToken = localStorage.getItem('adminToken');
    if (!adminToken) {
      navigate('/login');
    }
  }, [navigate]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch users
      const usersResponse = await axios.get(`${API_ADMIN_END_POINT}users`);
      setUsers(usersResponse.data);

      // Fetch content writers
      const contentWritersResponse = await axios.get(`${API_ADMIN_END_POINT}content-writers`);
      setContentWriters(contentWritersResponse.data);

      // Fetch blogs
      const blogsResponse = await axios.get(`${API_ADMIN_END_POINT}blogs`);
      setBlogs(blogsResponse.data);

      // Calculate stats
      setStats({
        totalUsers: usersResponse.data.length,
        totalBlogs: blogsResponse.data.length,
        totalContentWriters: contentWritersResponse.data.length,
        recentBlogs: blogsResponse.data.length > 5 ? 5 : blogsResponse.data.length
      });

      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to fetch data. Please try again later.');
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchDashboardData();
  };

  const handleLogout = () => {
    // Clear ALL authentication data from localStorage
    localStorage.removeItem('blogToken');
    localStorage.removeItem('blogUser');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    // Use a more forceful approach to ensure navigation happens
    setTimeout(() => {
      window.location.href = '/login';
    }, 100);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleEditBlog = (blog) => {
    setEditingBlog(blog);
    setActiveTab('edit-blog');
  };

  const handleDeleteBlog = async (blogId) => {
    if (window.confirm('Are you sure you want to delete this blog?')) {
      try {
        await axios.delete(`${API_END_POINT}astroblog/${blogId}`);
        fetchDashboardData();
      } catch (err) {
        setError('Failed to delete blog');
        console.error('Error deleting blog:', err);
      }
    }
  };

  const handleContentWriterSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');

    try {
      // Validate form fields
      if (!newContentWriter.username || !newContentWriter.email || !newContentWriter.password || !newContentWriter.name) {
        setFormError('All fields are required');
        return;
      }

      // Make API call to create content writer
      const response = await axios.post(`${API_ADMIN_END_POINT}create/content-writers`, newContentWriter);

      if (response.data) {
        setFormSuccess('Content writer created successfully!');
        // Reset form
        setNewContentWriter({
          username: '',
          email: '',
          password: '',
          name: ''
        });
        // Refresh content writers list
        fetchDashboardData();
      }
    } catch (error) {
      console.error('Error creating content writer:', error);
      setFormError(error.response?.data?.message || 'Failed to create content writer');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewContentWriter(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditWriter = (writer) => {
    setEditingWriter(writer);
    setNewContentWriter({
      username: writer.username || '',
      email: writer.email || '',
      password: '',
      name: writer.name || ''
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditingWriter(null);
    setNewContentWriter({
      username: '',
      email: '',
      password: '',
      name: ''
    });
    setIsEditing(false);
    setFormError('');
    setFormSuccess('');
  };

  const handleUpdateWriter = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');

    try {
      // Validate form fields
      if (!newContentWriter.username || !newContentWriter.email || !newContentWriter.name) {
        setFormError('Username, email, and name are required');
        return;
      }

      // Prepare data for update (only include password if it's provided)
      const updateData = {
        username: newContentWriter.username,
        email: newContentWriter.email,
        name: newContentWriter.name
      };

      if (newContentWriter.password) {
        updateData.password = newContentWriter.password;
      }

      // Make API call to update content writer
      const response = await axios.put(`${API_ADMIN_END_POINT}update/content-writers/${editingWriter._id}`, updateData);

      if (response.data) {
        setFormSuccess('Content writer updated successfully!');
        // Reset form and editing state
        setEditingWriter(null);
        setNewContentWriter({
          username: '',
          email: '',
          password: '',
          name: ''
        });
        setIsEditing(false);
        // Refresh content writers list
        fetchDashboardData();
      }
    } catch (error) {
      console.error('Error updating content writer:', error);
      setFormError(error.response?.data?.message || 'Failed to update content writer');
    }
  };

  const handleDeleteWriter = async (id) => {
    if (window.confirm('Are you sure you want to delete this content writer?')) {
      try {
        const response = await axios.delete(`${API_ADMIN_END_POINT}delete/content-writers/${id}`);

        if (response.data) {
          // Refresh content writers list
          fetchDashboardData();
        }
      } catch (error) {
        console.error('Error deleting content writer:', error);
        alert(error.response?.data?.message || 'Failed to delete content writer');
      }
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return <Overview stats={stats} />;
      case 'users':
        return <Users users={users} />;
      case 'content-writers':
        return (
          <ContentWriters
            contentWriters={contentWriters}
            newContentWriter={newContentWriter}
            isEditing={isEditing}
            formError={formError}
            formSuccess={formSuccess}
            handleInputChange={handleInputChange}
            handleContentWriterSubmit={handleContentWriterSubmit}
            handleUpdateWriter={handleUpdateWriter}
            handleCancelEdit={handleCancelEdit}
            handleEditWriter={handleEditWriter}
            handleDeleteWriter={handleDeleteWriter}
          />
        );
      case 'blogs':
        return (
          <Blogs
            blogs={blogs}
            loading={loading}
            error={error}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            handleRefresh={handleRefresh}
            handleEditBlog={handleEditBlog}
            handleDeleteBlog={handleDeleteBlog}
          />
        );
      case 'create-blog':
        return (
          <BlogCreate
            handleTabChange={handleTabChange}
          />
        );
      case 'edit-blog':
        return (
          <BlogEdit
            blogs={blogs}
            editingBlog={editingBlog}
            setEditingBlog={setEditingBlog}
            handleEdit={handleEditBlog}
          />
        );
      case 'queries':
        return <QueryDetails />;
      // case 'settings':
      //   return <Settings />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-r from-white to-success/40">
      <h1 className="text-center text-xs bg-secondary text-gray-200 py-1 md:mb-5">
        Welcome to the AstroTellers Admin Dashboard
      </h1>
      <div className="w-full md:w-[80%] mx-auto">
        <div className="flex flex-col md:flex-row">
          {/* Sidebar */}
          <div className={`fixed md:relative inset-y-0 left-0 z-50 md:z-0 w-64 bg-slate-100 shadow-lg transform transition-transform duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
            }`}>
            <div className="flex items-center justify-center p-4 border-b">
              <h1 className="text-xl font-bold text-center text-secondary">Admin Dashboard</h1>
              <button
                onClick={() => setIsSidebarOpen(false)}
                className="md:hidden text-gray-600 hover:text-gray-800"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>
            <nav className="p-4">
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => handleTabChange('overview')}
                    className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${activeTab === 'overview'
                      ? 'bg-secondary text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                      }`}
                  >
                    <FiHome className="w-5 h-5" />
                    Overview
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => handleTabChange('users')}
                    className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${activeTab === 'users'
                      ? 'bg-secondary text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                      }`}
                  >
                    <FiUsers className="w-5 h-5" />
                    Users
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => handleTabChange('content-writers')}
                    className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${activeTab === 'content-writers'
                      ? 'bg-secondary text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                      }`}
                  >
                    <FiUsers className="w-5 h-5" />
                    Content Writers
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => handleTabChange('blogs')}
                    className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${activeTab === 'blogs'
                      ? 'bg-secondary text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                      }`}
                  >
                    <FiFileText className="w-5 h-5" />
                    Blogs
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => handleTabChange('create-blog')}
                    className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${activeTab === 'create-blog'
                      ? 'bg-secondary text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                      }`}
                  >
                    <FiPlusSquare className="w-5 h-5" />
                    Create Blog
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => handleTabChange('queries')}
                    className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${activeTab === 'queries'
                      ? 'bg-secondary text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                      }`}
                  >
                    <FiMessageSquare className="w-5 h-5" />
                    Queries
                  </button>
                </li>
                {/* <li>
                  <button
                    onClick={() => handleTabChange('settings')}
                    className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                      activeTab === 'settings'
                        ? 'bg-secondary text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <FiSettings className="w-5 h-5" />
                    Settings
                  </button>
                </li> */}
                <li className="mt-8">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <FiLogOut className="w-5 h-5" />
                    Logout
                  </button>
                </li>
              </ul>
            </nav>
          </div>

          {/* Main Content */}
          <div className="md:w-3/4">
            {/* Mobile Header */}
            <div className="md:hidden bg-slate-100 shadow-sm p-2">
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="text-gray-600 hover:text-gray-800"
              >
                <FiMenu className="w-6 h-6" />
              </button>
            </div>

            {/* Content Area */}
            <main className="min-h-screen bg-slate-100">
              <div className='flex justify-center p-5 items-center'>
                <h1 className='text-3xl font-bold font-serif text-secondary'>AstroTellers Admin Panel</h1>
              </div>
              {renderContent()}
            </main>
          </div>
        </div>
      </div>
      {/* Footer */}
      <footer className="bg-secondary text-white py-2 mt-5">
        <div className="container mx-auto px-4">
          <p className="text-center">
            &copy; {new Date().getFullYear()} Astro Blog. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AdminDashboard;